import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import { ASSET_TYPE_PATH_SEPARATOR } from "./assetTypePath";

const ELLIPSIS = "...";

interface Props {
  /** The asset-type path, root → leaf (already depth-ordered). */
  segments: string[];
  /** Separator between levels. Defaults to " > ". */
  separator?: string;
  /**
   * Explicit width budget in px. Use this inside shrink-to-content containers such as the
   * `PrimaryTag` badge (which can't tell the child how much room it has). When omitted, the
   * widget fills its parent (width: 100%) and reacts to the parent's width via ResizeObserver —
   * use that inside containers that already have a definite width (table cells, page headers).
   */
  maxWidth?: number;
  className?: string;
  /** Tooltip; defaults to the full (un-collapsed) path. */
  title?: string;
}

// A single shared canvas — measuring text this way avoids the layout thrash of rendering each
// candidate string into the DOM.
let sharedCanvas: HTMLCanvasElement | null = null;
function measure(text: string, font: string): number {
  if (typeof document === "undefined") {
    return text.length * 7; // SSR / test fallback
  }
  if (!sharedCanvas) {
    sharedCanvas = document.createElement("canvas");
  }
  const ctx = sharedCanvas.getContext("2d");
  if (!ctx) {
    return text.length * 7;
  }
  ctx.font = font;
  return ctx.measureText(text).width;
}

// ONE fixed reference font for every chip, so the same path collapses to the same number of
// levels everywhere — independent of the chip's own font size (e.g. the header's smaller "sm").
// We measure at the larger "md" chip size (~13px) so a chip rendered smaller never overflows.
let cachedReferenceFont: string | null = null;
function referenceFont(): string {
  if (cachedReferenceFont) {
    return cachedReferenceFont;
  }
  if (typeof document === "undefined") {
    cachedReferenceFont = "13px sans-serif";
  } else {
    const family = window.getComputedStyle(document.body).fontFamily || "sans-serif";
    cachedReferenceFont = `13px ${family}`;
  }
  return cachedReferenceFont;
}

/**
 * Collapse the path to fit within `budget` px. Always keeps the root and the leaf; the leaf is
 * never dropped (it stays the right-most segment). Grows the visible window from both ends —
 * leaf side first, then root side — so wider containers reveal more context, matching
 * "Continent > Country > ... > Building > Floor".
 */
function collapseToFit(
  segments: string[],
  separator: string,
  budget: number,
  measureText: (s: string) => number
): string {
  const n = segments.length;
  if (n === 0) {
    return "";
  }
  if (n === 1) {
    return segments[0];
  }
  const full = segments.join(separator);
  if (budget <= 0 || measureText(full) <= budget) {
    return full;
  }

  const leaf = segments[n - 1];
  const build = (head: number, tail: number): string => {
    if (head + tail >= n) {
      return full;
    }
    return [
      ...segments.slice(0, head),
      ELLIPSIS,
      ...segments.slice(n - tail),
    ].join(separator);
  };

  // Minimum that still keeps root + leaf.
  if (measureText(build(1, 1)) > budget) {
    // Even "root > ... > leaf" overflows — keep the leaf visible by dropping the root.
    const tailOnly = ELLIPSIS + separator + leaf;
    return measureText(tailOnly) <= budget ? tailOnly : leaf;
  }

  // Greedily widen the window (leaf side first, then root side) while it still fits.
  let head = 1;
  let tail = 1;
  let best = build(head, tail);
  let grew = true;
  while (grew && head + tail < n) {
    grew = false;
    if (measureText(build(head, tail + 1)) <= budget) {
      tail += 1;
      best = build(head, tail);
      grew = true;
      continue;
    }
    if (measureText(build(head + 1, tail)) <= budget) {
      head += 1;
      best = build(head, tail);
      grew = true;
    }
  }
  return best;
}

/**
 * Smart, size-aware asset-type path renderer. Renders the full breadcrumb when it fits and
 * collapses the middle to "root > ... > leaf" (keeping as much context as fits) when it doesn't —
 * based on the actual available width, not a fixed node count. The full path is always available
 * via the tooltip.
 */
const SmartAssetTypePath: React.FC<Props> = ({
  segments,
  separator = ASSET_TYPE_PATH_SEPARATOR,
  maxWidth,
  className,
  title,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const fullPath = useMemo(() => segments.join(separator), [segments, separator]);
  const [display, setDisplay] = useState(fullPath);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      setDisplay(fullPath);
      return;
    }
    const recompute = () => {
      // Measure with ONE shared reference font so every chip (header "sm", field "md", list, …)
      // collapses the SAME path to the SAME number of levels — the level count must be identical
      // everywhere, not vary with each chip's own font size.
      const font = referenceFont();
      // 1px safety margin so sub-pixel rounding never clips the last fitting segment.
      const budget = (maxWidth != null ? maxWidth : el.clientWidth) - 1;
      setDisplay(collapseToFit(segments, separator, budget, (s) => measure(s, font)));
    };
    recompute();
    if (maxWidth == null && typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(recompute);
      observer.observe(el);
      return () => observer.disconnect();
    }
    return undefined;
  }, [segments, separator, maxWidth, fullPath]);

  return (
    <span
      ref={ref}
      className={className}
      title={title != null ? title : fullPath}
      style={{
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        ...(maxWidth != null ? { maxWidth } : { width: "100%" }),
      }}
    >
      {display}
    </span>
  );
};

export default SmartAssetTypePath;
